import { distance } from 'fastest-levenshtein';
import { v4 as uuid } from 'uuid';
import type { WizardState, ColumnMapping } from '../wizardReducer';
import type { NameColumnConfig } from '../parsers/types';
import { addAmounts, getEmployeeName, normalizeEmployeeName, parseNumber } from '../parsers';

// Step 3 fuzzy employee matching (WS-10, Functional Spec §5.2).
//
// `fastest-levenshtein` exposes only `distance(a, b)` — a raw integer edit
// distance, NOT a similarity ratio. The 0–100 score below is WS-10's own: a
// weighted blend of normalized Levenshtein similarity and token-overlap (Jaccard)
// so that reordered / partial names ("John A. Smith" vs "Smith, John") aren't
// unfairly penalized by raw edit distance. Thresholds are conservative and tunable
// after QA against real provider files.

export const MATCH_FLOOR = 60; // <60 → 'new'
export const AUTO_ACCEPT = 85; // ≥85 → 'matched'
export const LEV_WEIGHT = 0.6; // weight for Levenshtein similarity
export const TOKEN_WEIGHT = 0.4; // weight for token Jaccard overlap

// Minimal shape the matcher needs from a roster employee. The live
// `POST /employees/list` payload carries more fields; structural typing lets the
// caller pass the full objects.
export interface RosterEmployee {
	id: string;
	fullName: string;
	isActiveInApp: boolean;
}

// Split into lowercased, punctuation-stripped, non-empty tokens.
function tokenize(s: string): Set<string> {
	return new Set(
		s
			.split(/\s+/)
			.map((t) => t.toLowerCase().replace(/[^a-z0-9]/g, ''))
			.filter((t) => t !== '')
	);
}

function jaccard(a: Set<string>, b: Set<string>): number {
	if (a.size === 0 || b.size === 0) return 0;
	let intersection = 0;
	a.forEach((t) => {
		if (b.has(t)) intersection += 1;
	});
	const union = a.size + b.size - intersection;
	return union === 0 ? 0 : intersection / union;
}

/**
 * 0–100 similarity score between two (pre-normalized) names. Exact
 * case-insensitive equality short-circuits to 100. Otherwise blends normalized
 * Levenshtein similarity with token Jaccard overlap per LEV_WEIGHT / TOKEN_WEIGHT.
 */
export function scoreNames(a: string, b: string): number {
	if (a.toLowerCase() === b.toLowerCase()) return 100;

	const maxLen = Math.max(a.length, b.length);
	const levSim = maxLen === 0 ? 1 : 1 - distance(a, b) / maxLen;
	const tokenSim = jaccard(tokenize(a), tokenize(b));

	return Math.round(100 * (LEV_WEIGHT * levSim + TOKEN_WEIGHT * tokenSim));
}

/** Band a 0–100 score into a match status. Boundaries are inclusive at the floor. */
export function bandFor(score: number): 'matched' | 'review' | 'new' {
	if (score >= AUTO_ACCEPT) return 'matched';
	if (score >= MATCH_FLOOR) return 'review';
	return 'new';
}

/**
 * Resolve every aggregated row to a match status against the company roster.
 *
 * - `aliasMap` (csvValue → employeeId) short-circuits scoring entirely for names
 *   resolved by a previously-saved CsvValueMapping, as long as that employee is
 *   still in the roster. Those rows are 'saved' (blue trust badge).
 * - Otherwise the best score over the roster decides the band:
 *   ≥85 'matched', 60–84 'review', <60 'new'.
 */
export function computeEmployeeRows(
	aggregatedRows: WizardState['aggregatedRows'],
	roster: RosterEmployee[],
	aliasMap: Map<string, string>
): WizardState['employeeRows'] {
	const rosterById = new Map(roster.map((e) => [e.id, e]));
	const isInactive = (id: string | null): boolean =>
		id != null && rosterById.get(id)?.isActiveInApp === false;

	return aggregatedRows.map((row) => {
		const normalizedName = normalizeEmployeeName(row.csvName);

		// 0. Blank source name (ENG-369). A row whose name cell was empty must not be
		// treated as a match candidate or pre-resolved as a new employee — it has no
		// name to match, score, or create under. Flag it 'blank_name' so Step 3 shows
		// it as an unresolved error and blocks advance until the user maps it to a real
		// employee, adds it as new with a typed name, or Skips it. addAsNewName stays
		// null so it can't leak a blank fullName into the Confirm payload.
		if (row.csvName.trim() === '') {
			return {
				rowId: row.rowId,
				csvName: row.csvName,
				normalizedName,
				matchStatus: 'blank_name' as const,
				matchScore: null,
				matchedEmployeeId: null,
				matchedEmployeeName: null,
				addAsNewName: null,
				inactive: false,
				fuzzyScore: null,
				skipped: false,
				confidence: row.confidence,
			};
		}

		// 1. Alias short-circuit — only when the aliased employee still exists.
		const aliasEmployeeId = aliasMap.get(row.csvName);
		if (aliasEmployeeId && rosterById.has(aliasEmployeeId)) {
			return {
				rowId: row.rowId,
				csvName: row.csvName,
				normalizedName,
				matchStatus: 'saved' as const,
				matchScore: null,
				matchedEmployeeId: aliasEmployeeId,
				// ENG-410: capture the CAP display name for Step 4's preview.
				matchedEmployeeName: rosterById.get(aliasEmployeeId)?.fullName ?? null,
				addAsNewName: null,
				inactive: isInactive(aliasEmployeeId),
				fuzzyScore: null,
				// ENG-414: rows start un-skipped; Skip is a user action in Step 3.
				skipped: false,
				confidence: row.confidence,
			};
		}

		// 2. Best fuzzy score over the roster.
		let bestScore = -1;
		let bestId: string | null = null;
		for (const emp of roster) {
			const score = scoreNames(
				normalizedName,
				normalizeEmployeeName(emp.fullName)
			);
			if (score > bestScore) {
				bestScore = score;
				bestId = emp.id;
			}
		}

		// 3. Band the best score.
		const hasScore = bestScore >= 0;
		const isExact = bestScore === 100;
		const matchStatus = hasScore ? bandFor(bestScore) : 'new';
		const matchedEmployeeId = matchStatus === 'new' ? null : bestId;

		return {
			rowId: row.rowId,
			csvName: row.csvName,
			normalizedName,
			matchStatus,
			// matchScore drives the inline "Review · NN" badge; null for exact
			// matches (and 'saved' above) per the WizardState contract.
			matchScore: hasScore && !isExact ? bestScore : null,
			matchedEmployeeId,
			// ENG-410: display name (fullName) of the matched employee for Step 4;
			// null for new-band rows (no matchedEmployeeId).
			matchedEmployeeName:
				matchedEmployeeId != null
					? rosterById.get(matchedEmployeeId)?.fullName ?? null
					: null,
			// New-band rows are resolved by default: pre-populate the add-as-new
			// name with the source name so they need no user action to advance.
			addAsNewName: matchStatus === 'new' ? row.csvName : null,
			inactive: isInactive(matchedEmployeeId),
			fuzzyScore: hasScore ? bestScore : null,
			// ENG-414: rows start un-skipped; Skip is a user action in Step 3.
			skipped: false,
			confidence: row.confidence,
		};
	});
}

// Cost-column key used in aggregatedRows.values — the field's stable identifier
// from the Step 2 mapping (existing fieldId or pending tempId). Step 4 reads
// values by this same key.
function costKey(m: ColumnMapping): string | null {
	if (m.fieldRef.type === 'existing') return m.fieldRef.fieldId;
	if (m.fieldRef.type === 'pending') return m.fieldRef.tempId;
	return null;
}

/**
 * Adapt the within-file aggregator's `string[][]` matrix into the typed
 * `aggregatedRows` state shape. Numeric cost columns are parsed and summed by
 * field key (multiple source columns mapped to the same field add together),
 * with the Step 2 Convert (+/-) toggle applied here.
 *
 * @param matrix       output of `aggregateByEmployee` (one row per employee)
 * @param nameColConfig the resolved name-column layout (for csvName extraction)
 * @param costMappings Step 2 mappings already filtered to real cost columns
 *                     (category is a sectionId and fieldRef !== 'none')
 * @param rowConfidence P2.2: output of `confidenceByEmployee`, same order as
 *                     `matrix` — omitted (and every row's confidence stays null)
 *                     for CSV/XLSX imports, which have no AI confidence at all.
 */
export function buildAggregatedRows(
	matrix: string[][],
	nameColConfig: NonNullable<NameColumnConfig>,
	costMappings: ColumnMapping[],
	rowConfidence?: Array<'high' | 'low'>
): WizardState['aggregatedRows'] {
	return matrix.map((row, i) => {
		const csvName = getEmployeeName(row, nameColConfig);
		const values: Record<string, number> = {};
		for (const m of costMappings) {
			const key = costKey(m);
			if (key === null) continue;
			const parsed = parseNumber(row[m.colIndex]);
			const value = Number.isNaN(parsed) ? 0 : parsed;
			values[key] = addAmounts(values[key] ?? 0, m.negate ? -value : value);
		}
		return { rowId: uuid(), csvName, values, confidence: rowConfidence?.[i] ?? null };
	});
}
